import PageLoader from '@components/PageLoader'
import { useAdminExpense } from '@hooks/useAdminExpense'
import React from 'react'
import { useParams } from 'react-router-dom'
import { formatAmount, formatDate, formatDateTime, formatName } from '../adminFormat'
import {
	BackLink,
	Card,
	CardTitle,
	DeletedBadge,
	DetailHeader,
	DetailTitle,
	EmptyState,
	ErrorState,
	Field,
	FieldGrid,
	FieldLabel,
	FieldValue,
	InlineLink,
	RelatedList,
	RelatedMeta,
	RelatedRow,
} from '../adminDetail.styles'

/**
 * Read-only admin detail for one expense: its trip (linked), payer (linked), split
 * type, participants (each linked, with their split share), and timestamps. Amounts
 * stay in their own currency — no FX conversion. No mutations.
 */
const ExpenseDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const { data: expense, isLoading, isError } = useAdminExpense(Number(id))

	return (
		<>
			<BackLink to="/admin/expenses">← Back to expenses</BackLink>

			{isLoading ? (
				<PageLoader label="Loading expense…" />
			) : isError || !expense ? (
				<ErrorState>Could not load this expense.</ErrorState>
			) : (
				<>
					<DetailHeader>
						<DetailTitle>{expense.name}</DetailTitle>
						{expense.isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
					</DetailHeader>

					<Card>
						<CardTitle>Expense</CardTitle>
						<FieldGrid>
							<Field>
								<FieldLabel>Amount</FieldLabel>
								<FieldValue>{formatAmount(expense.amount, expense.currencyCode)}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Trip</FieldLabel>
								<FieldValue>
									<InlineLink to={`/admin/trips/${expense.tripId}`}>{expense.tripName}</InlineLink>
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Paid by</FieldLabel>
								<FieldValue>
									<InlineLink to={`/admin/users/${expense.paidBy.id}`}>
										{formatName(expense.paidBy.firstName, expense.paidBy.lastName)}
									</InlineLink>
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Split</FieldLabel>
								<FieldValue>{expense.splitType}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Expense date</FieldLabel>
								<FieldValue>{formatDate(expense.expenseDate)}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Created</FieldLabel>
								<FieldValue>{formatDateTime(expense.createdAt)}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Last updated</FieldLabel>
								<FieldValue>{formatDateTime(expense.lastUpdatedAt)}</FieldValue>
							</Field>
						</FieldGrid>
					</Card>

					<Card>
						<CardTitle>Participants ({expense.participants.length})</CardTitle>
						{expense.participants.length === 0 ? (
							<EmptyState>No participants on this expense.</EmptyState>
						) : (
							<RelatedList>
								{expense.participants.map(participant => (
									<RelatedRow key={participant.userId} to={`/admin/users/${participant.userId}`}>
										<span>{formatName(participant.firstName, participant.lastName)}</span>
										<RelatedMeta>
											{participant.splitAmount != null
												? formatAmount(participant.splitAmount, expense.currencyCode)
												: 'Equal share'}
										</RelatedMeta>
									</RelatedRow>
								))}
							</RelatedList>
						)}
					</Card>
				</>
			)}
		</>
	)
}

export default ExpenseDetailPage
